import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StepperFooter,
  useToast,
} from '@folio/components';
import { useAssertedParams } from '@folio/lib/utils';
import { DocumentsListsPolicyModule } from '../DocumentsListsPolicyModule/DocumentsListsPolicyModule';
import { QuoteTierModule } from '../QuoteTierModule/QuoteTierModule';
import { useQuery } from '@tanstack/react-query';
import {
  brokerageFirmControllerGetBrokerageFirmQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  Customer,
  GetBrokerageFirmOutputFirmTypeEnumKey,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
  legacyPermissionsControllerAtMeQueryOptions,
  policyControllerGetPolicyQueryOptions,
} from 'korint-sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Group, Stack } from '@folio/components/ui/flex';

type FunnelEndModuleProps = {
  id: string;
  callback: {
    callToActionCallback?: (props: {
      policyId: string;
      branchId: string;
      customer: Customer;
    }) => Promise<void> | void;
  };
  additionalComponents: {
    showDocumentsGeneratedAtQuote?: boolean;
    isQuoteTierModuleVisible: boolean;
    callToActionButton?: {
      hiddenForFirmTypes?: GetBrokerageFirmOutputFirmTypeEnumKey[];
    };
    finishWithoutSendingButton?: {
      enabled?: boolean;
    };
  };
  redirectTo?: (props: { policyId: string; branchId: string; customer: Customer }) => string;
};

export function FunnelEndModule({
  id,
  callback,
  additionalComponents,
  redirectTo,
}: FunnelEndModuleProps) {
  const { t } = useTranslation();
  const { policyId, branchId, customerId } = useAssertedParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
  });

  const { data: customer } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId, { branchId }),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { data: policy } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId, { branchId }),
  });

  const { data: brokerageFirm } = useQuery({
    ...brokerageFirmControllerGetBrokerageFirmQueryOptions(user?.brokerageFirmId!),
    enabled: Boolean(user?.brokerageFirmId),
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { data: documentsGeneratedAtQuote = [] } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy?.productId!),
    enabled: Boolean(policy?.productId && additionalComponents.showDocumentsGeneratedAtQuote),
    select: ({ generatedDocuments }) =>
      generatedDocuments
        .filter(
          ({ trigger }) =>
            trigger.includes('POLICY_NB_QUOTED') ||
            trigger.includes('POLICY_MTA_QUOTED') ||
            trigger.includes('POLICY_NB_CONFIRMED') ||
            trigger.includes('POLICY_MTA_CONFIRMED'),
        )
        .map(({ documentType, configName }) => documentType ?? configName),
  });

  const toast = useToast();
  const navigate = useNavigate();
  const redirectUrl =
    redirectTo?.({ policyId, branchId, customer: customer! }) ??
    `/policies/${policyId}/${branchId}`;
  const firmContext = brokerageFirm?.firmType === 'WHOLESALE' ? 'WHOLESALE' : '';
  const isCallToActionHidden = Boolean(
    brokerageFirm?.firmType &&
      additionalComponents.callToActionButton?.hiddenForFirmTypes?.includes(brokerageFirm.firmType),
  );

  if (!policy?.productId) {
    return null;
  }
  const ns = `${policy.productId}.funnelEnd.${id}`;
  const fallbackNs = `funnelEnd.${id}`;

  return (
    <Stack>
      <Card>
        <CardHeader>
          <CardTitle>{t([`${ns}.title`, `${fallbackNs}.title`])}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <p className="whitespace-pre-line">
            {t([`${ns}.message`, `${fallbackNs}.message`], { context: firmContext })}
          </p>
        </CardContent>
      </Card>

      {additionalComponents.showDocumentsGeneratedAtQuote &&
        documentsGeneratedAtQuote.length > 0 && (
          <DocumentsListsPolicyModule
            displayContext="policyholder"
            categories={documentsGeneratedAtQuote}
            pollUntilAllDocumentsAreLoaded={true}
          />
        )}

      {additionalComponents.isQuoteTierModuleVisible && (
        <QuoteTierModule showModifyButton={false} />
      )}

      <Group align="stretch" justify="between" gap={0}>
        <StepperFooter nextButtonEnabled={false} showNextButton={false} showPreviousButton={true} />
        <Group align="stretch" gap={2}>
          {(policy?.branchType === 'NEW_BUSINESS' || policy?.branchType === 'MTA') &&
            additionalComponents.finishWithoutSendingButton?.enabled !== false && (
              <Link to={redirectUrl}>
                <Button variant="outline">
                  {t([
                    `${ns}.callback.finishWithoutSendingButtonLabel`,
                    `${fallbackNs}.callback.finishWithoutSendingButtonLabel`,
                  ])}
                </Button>
              </Link>
            )}
          {!isCallToActionHidden && (
            <Button
              loading={isSubmitting}
              onClick={async () => {
                if (!customer || isSubmitting) {
                  return;
                }
                setIsSubmitting(true);
                if (callback.callToActionCallback) {
                  try {
                    await callback.callToActionCallback({
                      policyId,
                      customer,
                      branchId,
                    });
                    navigate(redirectUrl);

                    toast.toast({
                      title: t([
                        `${ns}.callback.success.title`,
                        `${fallbackNs}.callback.success.title`,
                      ]),
                      description: t([
                        `${ns}.callback.success.description`,
                        `${fallbackNs}.callback.success.description`,
                      ]),
                    });
                  } catch (error) {
                    toast.toast({
                      title: t([
                        `${ns}.callback.error.title`,
                        `${fallbackNs}.callback.error.title`,
                      ]),
                      description: t([
                        `${ns}.callback.error.description`,
                        `${fallbackNs}.callback.error.description`,
                      ]),
                    });
                    setIsSubmitting(false);
                    return;
                  }
                } else {
                  navigate(redirectUrl);
                }
              }}
            >
              {t(
                [
                  `${ns}.callback.callToActionButtonLabel`,
                  `${fallbackNs}.callback.callToActionButtonLabel`,
                ],
                {
                  context: firmContext,
                },
              )}
            </Button>
          )}
        </Group>
      </Group>
    </Stack>
  );
}
